import { z } from 'zod';

/**
 * Sales: a file per customer (firm or individual) holding services — flights
 * first; hotels, cars and extras later. Request schemas and the rules every
 * itinerary must satisfy, shared by API and console.
 */

const id = z.uuid();
const version = z.number().int().positive();
const blankToNull = <T>(v: T) => (v === '' ? null : v);
const optionalText = (max: number) =>
  z.string().trim().max(max).nullable().optional().transform(blankToNull);

export const CURRENCIES = ['TRY', 'EUR', 'USD', 'GBP'] as const;
export type Currency = (typeof CURRENCIES)[number];
export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  TRY: '₺',
  EUR: '€',
  USD: '$',
  GBP: '£',
};

/**
 * Money arrives as a number or a decimal string ("1240.5", "1240,50") and leaves
 * as a fixed two-decimal string, which is how Postgres numeric returns it too.
 */
export const money = z.union([z.number(), z.string()]).transform((v, ctx) => {
  const n = typeof v === 'number' ? v : Number(v.trim().replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(n) || n < 0 || n > 9_999_999_999.99) {
    ctx.addIssue({ code: 'custom', message: 'Geçerli bir tutar girin' });
    return z.NEVER;
  }
  return n.toFixed(2);
});

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarih seçin');
const clock = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Saat SS:DD biçiminde olmalı')
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));

const airport = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, 'Havalimanı kodunu seçin');

export const TRIP_TYPES = ['one_way', 'round_trip', 'multi_city'] as const;
export type TripType = (typeof TRIP_TYPES)[number];
export const TRIP_TYPE_LABELS: Record<TripType, string> = {
  one_way: 'Tek yön',
  round_trip: 'Gidiş-dönüş',
  multi_city: 'Çoklu uçuş',
};

export const CABINS = ['economy', 'premium_economy', 'business', 'first'] as const;
export const CABIN_LABELS: Record<(typeof CABINS)[number], string> = {
  economy: 'Ekonomi',
  premium_economy: 'Premium ekonomi',
  business: 'Business',
  first: 'First',
};

export const PASSENGER_TYPES = ['adult', 'child', 'infant'] as const;
export type PassengerType = (typeof PASSENGER_TYPES)[number];
export const PASSENGER_TYPE_LABELS: Record<PassengerType, string> = {
  adult: 'Yetişkin',
  child: 'Çocuk',
  infant: 'Bebek',
};

/** Airline fare rules: under 2 is an infant, under 12 a child, on the travel date. */
export function passengerTypeFor(
  birthDate: string | null,
  travelDate: string | null,
): PassengerType {
  if (!birthDate || !travelDate) return 'adult';
  const [by, bm, bd] = birthDate.split('-').map(Number);
  const [ty, tm, td] = travelDate.split('-').map(Number);
  let age = ty! - by!;
  if (tm! < bm! || (tm === bm && td! < bd!)) age--;
  if (age < 2) return 'infant';
  if (age < 12) return 'child';
  return 'adult';
}

export const SERVICE_STATUSES = ['option', 'confirmed', 'cancelled'] as const;
export type ServiceStatus = (typeof SERVICE_STATUSES)[number];
/** Flight wording; hotels and cars will say "Onaylandı" for confirmed. */
export const FLIGHT_STATUS_LABELS: Record<ServiceStatus, string> = {
  option: 'Opsiyonda',
  confirmed: 'Biletlendi',
  cancelled: 'İptal',
};

export const flightSegmentInput = z.object({
  origin: airport,
  destination: airport,
  departureDate: isoDate,
  departureTime: clock,
  arrivalDate: isoDate
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  arrivalTime: clock,
  airline: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{2}$/, 'Havayolunu seçin')
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  flightNumber: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^\d{1,4}[A-Z]?$/, 'Uçuş numarası rakamlardan oluşmalı (örn. 1523)')
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
});
export type FlightSegmentInput = z.input<typeof flightSegmentInput>;

const passengerInput = z.object({
  contactId: id,
  type: z.enum(PASSENGER_TYPES).default('adult'),
  ticketNumber: z
    .string()
    .trim()
    .transform((v) => v.replace(/\s/g, ''))
    .pipe(z.string().regex(/^[0-9-]{0,20}$/, 'Bilet numarası rakam ve tireden oluşmalı'))
    .nullable()
    .optional()
    .transform(blankToNull),
});

/** What every kind of service carries: status, price, supplier, notes. */
const commonFields = {
  status: z.enum(['option', 'confirmed']).default('confirmed'),
  optionDeadline: z.iso
    .datetime({ offset: true })
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  currency: z.enum(CURRENCIES),
  cost: money,
  price: money,
  /** Who it is bought from; must provide this kind of service. */
  supplierId: id
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  notes: optionalText(4000),
};

function checkOption(
  f: { status: 'option' | 'confirmed'; optionDeadline?: string | null },
  ctx: z.RefinementCtx,
) {
  if (f.status === 'option' && !f.optionDeadline) {
    ctx.addIssue({
      code: 'custom',
      path: ['optionDeadline'],
      message: 'Opsiyon son tarihini girin',
    });
  }
}

const flightFields = z.object({
  tripType: z.enum(TRIP_TYPES),
  legs: z
    .array(z.object({ segments: z.array(flightSegmentInput).min(1).max(6) }))
    .min(1)
    .max(8),
  pnr: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{0,10}$/, 'PNR harf ve rakamlardan oluşmalı')
    .nullable()
    .optional()
    .transform(blankToNull),
  cabin: z.enum(CABINS).default('economy'),
  baggage: optionalText(60),
  passengers: z.array(passengerInput).min(1, 'En az bir yolcu ekleyin').max(99),
  ...commonFields,
});

/**
 * The rules an itinerary must satisfy, each reported on the field that breaks it
 * so the form can point at it.
 */
function checkFlight(f: z.output<typeof flightFields>, ctx: z.RefinementCtx) {
  const legCount = { one_way: 1, round_trip: 2, multi_city: 0 }[f.tripType];
  if (legCount && f.legs.length !== legCount) {
    ctx.addIssue({
      code: 'custom',
      path: ['legs'],
      message:
        f.tripType === 'round_trip' ? 'Gidiş-dönüşte iki uçuş olmalı' : 'Tek yönde bir uçuş olmalı',
    });
  }
  if (f.tripType === 'multi_city' && f.legs.length < 2) {
    ctx.addIssue({ code: 'custom', path: ['legs'], message: 'Çoklu uçuşta en az iki uçuş olmalı' });
  }

  let previousDeparture = '';
  f.legs.forEach((leg, l) => {
    leg.segments.forEach((s, i) => {
      const at = ['legs', l, 'segments', i];
      if (s.origin === s.destination) {
        ctx.addIssue({
          code: 'custom',
          path: [...at, 'destination'],
          message: 'Kalkış ve varış aynı olamaz',
        });
      }
      const next = leg.segments[i + 1];
      if (next && next.origin !== s.destination) {
        ctx.addIssue({
          code: 'custom',
          path: ['legs', l, 'segments', i + 1, 'origin'],
          message: `Aktarma ${s.destination} havalimanından kalkmalı`,
        });
      }
      const departure = `${s.departureDate}T${s.departureTime ?? '00:00'}`;
      if (previousDeparture && departure < previousDeparture) {
        ctx.addIssue({
          code: 'custom',
          path: [...at, 'departureDate'],
          message: 'Bu uçuş bir önceki uçuştan önce olamaz',
        });
      }
      previousDeparture = departure;
      if (s.arrivalDate && s.arrivalDate < s.departureDate) {
        ctx.addIssue({
          code: 'custom',
          path: [...at, 'arrivalDate'],
          message: 'Varış kalkıştan önce olamaz',
        });
      }
    });
  });

  const seen = new Set<string>();
  f.passengers.forEach((p, i) => {
    if (seen.has(p.contactId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['passengers', i],
        message: 'Bu yolcu listede zaten var',
      });
    }
    seen.add(p.contactId);
  });

  checkOption(f, ctx);
}

export const flightInput = flightFields.superRefine(checkFlight);
export type FlightInput = z.input<typeof flightInput>;

// Hotels -----------------------------------------------------------------------------

export const ROOM_TYPES = [
  'single',
  'double',
  'twin',
  'triple',
  'family',
  'suite',
  'other',
] as const;
export type RoomType = (typeof ROOM_TYPES)[number];
export const ROOM_TYPE_LABELS: Record<RoomType, string> = {
  single: 'Tek kişilik',
  double: 'Çift kişilik',
  twin: 'İki yataklı',
  triple: 'Üç kişilik',
  family: 'Aile odası',
  suite: 'Suit',
  other: 'Diğer',
};
/** The name a room goes by: the hotel's own ("Superior Twin") when written in. */
export const roomTypeName = (r: { type: RoomType; typeOther?: string | null }) =>
  r.type === 'other' ? (r.typeOther ?? ROOM_TYPE_LABELS.other) : ROOM_TYPE_LABELS[r.type];

/** The usual room for this many guests; twin and suite are chosen by hand. */
export function roomTypeFor(guests: number): RoomType {
  if (guests <= 1) return 'single';
  if (guests === 2) return 'double';
  if (guests === 3) return 'triple';
  return 'family';
}

export const BOARD_TYPES = [
  'room_only',
  'bed_breakfast',
  'half_board',
  'full_board',
  'all_inclusive',
  'ultra_all_inclusive',
] as const;
export type BoardType = (typeof BOARD_TYPES)[number];
export const BOARD_LABELS: Record<BoardType, string> = {
  room_only: 'Sadece oda',
  bed_breakfast: 'Oda + kahvaltı',
  half_board: 'Yarım pansiyon',
  full_board: 'Tam pansiyon',
  all_inclusive: 'Her şey dahil',
  ultra_all_inclusive: 'Ultra her şey dahil',
};
/** The codes hotels and suppliers use on vouchers. */
export const BOARD_CODES: Record<BoardType, string> = {
  room_only: 'RO',
  bed_breakfast: 'BB',
  half_board: 'HB',
  full_board: 'FB',
  all_inclusive: 'AI',
  ultra_all_inclusive: 'UAI',
};

export const HOTEL_STATUS_LABELS: Record<ServiceStatus, string> = {
  option: 'Opsiyonda',
  confirmed: 'Onaylandı',
  cancelled: 'İptal',
};

/** Nights between two ISO dates (check-in and check-out). */
export function nightsBetween(checkIn: string, checkOut: string): number {
  const ms = Date.parse(`${checkOut}T00:00:00Z`) - Date.parse(`${checkIn}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

const blankToNullish = (v: unknown) => (v === '' || v === undefined ? null : v);
/** A number the form may leave empty; written with a comma or a dot. */
const optionalNumber = (min: number, max: number, message: string, decimals = 0) =>
  z.preprocess(
    (v) => {
      const b = blankToNullish(v);
      return typeof b === 'string' ? Number(b.trim().replace(',', '.')) : b;
    },
    z
      .number({ error: message })
      .min(min, message)
      .max(max, message)
      .transform((n) => Number(n.toFixed(decimals)))
      .nullable(),
  );
const optionalMoney = z.preprocess(blankToNullish, money.nullable());

/**
 * What a room offer says beyond its type: the hotel's own name for it when the type
 * is Diğer, its size, and its price when the agency prices room by room (ADR 0018).
 */
const roomDetails = {
  type: z.enum(ROOM_TYPES),
  typeOther: optionalText(60),
  sizeM2: optionalNumber(1, 2000, 'Oda büyüklüğünü m² olarak yazın'),
  price: optionalMoney,
};

const hotelFields = z.object({
  hotelName: z.string().trim().min(1, 'Otel adını yazın').max(160),
  city: optionalText(80),
  /** Stars, 1–5, and the guests' score, 0–10 (8,2). */
  stars: optionalNumber(1, 5, 'Yıldız 1 ile 5 arasında olmalı'),
  rating: optionalNumber(0, 10, 'Puan 0 ile 10 arasında olmalı', 1),
  checkIn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Giriş tarihini seçin'),
  checkOut: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Çıkış tarihini seçin'),
  board: z.enum(BOARD_TYPES).default('bed_breakfast'),
  rooms: z
    .array(
      z.object({
        ...roomDetails,
        guests: z
          .array(z.object({ contactId: id }))
          .min(1, 'Odaya en az bir misafir ekleyin')
          .max(8, 'Bir odada en fazla 8 misafir olabilir'),
      }),
    )
    .min(1, 'En az bir oda ekleyin')
    .max(30),
  confirmationNumber: z.string().trim().max(40).nullable().optional().transform(blankToNull),
  cancelDeadline: isoDate
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  cancelPolicy: optionalText(1000),
  ...commonFields,
});

function checkHotel(h: z.output<typeof hotelFields>, ctx: z.RefinementCtx) {
  if (h.checkOut <= h.checkIn) {
    ctx.addIssue({
      code: 'custom',
      path: ['checkOut'],
      message: 'Çıkış tarihi girişten sonra olmalı',
    });
  } else if (nightsBetween(h.checkIn, h.checkOut) > 365) {
    ctx.addIssue({ code: 'custom', path: ['checkOut'], message: 'Konaklama bir yılı geçemez' });
  }
  const priced = h.rooms.filter((r) => r.price !== null).length;
  h.rooms.forEach((room, r) => {
    if (room.type === 'other' && !room.typeOther) {
      ctx.addIssue({
        code: 'custom',
        path: ['rooms', r, 'typeOther'],
        message: 'Oda tipini yazın',
      });
    }
    if (priced && room.price === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['rooms', r, 'price'],
        message: 'Bütün odalara fiyat yazın ya da hiçbirine yazmayın',
      });
    }
  });
  const seen = new Set<string>();
  h.rooms.forEach((room, r) =>
    room.guests.forEach((g, i) => {
      if (seen.has(g.contactId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['rooms', r, 'guests', i],
          message: 'Bu misafir başka bir odada da var',
        });
      }
      seen.add(g.contactId);
    }),
  );
  checkOption(h, ctx);
}

export const hotelInput = hotelFields.superRefine(checkHotel);

/** The sum of the rooms' prices when every room has one; otherwise null. */
export function roomPricesTotal(rooms: readonly { price: string | null }[]): string | null {
  if (!rooms.length || rooms.some((r) => r.price === null)) return null;
  return rooms.reduce((sum, r) => sum + Number(r.price), 0).toFixed(2);
}

/** A hotel priced room by room costs the customer what its rooms add up to. */
export function withRoomPrices<T extends { price: string; rooms: { price: string | null }[] }>(
  h: T,
): T {
  const total = roomPricesTotal(h.rooms);
  return total === null ? h : { ...h, price: total };
}
export type HotelInput = z.input<typeof hotelInput>;

// Car rentals --------------------------------------------------------------------------

export const CAR_CLASSES = [
  'mini',
  'economy',
  'compact',
  'intermediate',
  'fullsize',
  'premium',
  'suv',
  'van',
] as const;
export type CarClass = (typeof CAR_CLASSES)[number];
export const CAR_CLASS_LABELS: Record<CarClass, string> = {
  mini: 'Mini',
  economy: 'Ekonomi',
  compact: 'Kompakt',
  intermediate: 'Orta',
  fullsize: 'Büyük',
  premium: 'Premium',
  suv: 'SUV',
  van: 'Minibüs / Van',
};
/** What each class usually means, for people who do not know the codes. */
export const CAR_CLASS_HINTS: Record<CarClass, string> = {
  mini: 'Fiat 500, Kia Picanto',
  economy: 'Renault Clio, Fiat Egea',
  compact: 'VW Golf, Toyota Corolla',
  intermediate: 'Skoda Octavia, Peugeot 508',
  fullsize: 'VW Passat, BMW 3 Serisi',
  premium: 'Mercedes E, BMW 5 Serisi',
  suv: 'Nissan Qashqai, VW Tiguan',
  van: 'VW Transporter, Mercedes Vito',
};

export const TRANSMISSIONS = ['manual', 'automatic'] as const;
export const TRANSMISSION_LABELS: Record<(typeof TRANSMISSIONS)[number], string> = {
  manual: 'Manuel',
  automatic: 'Otomatik',
};

export const CAR_INCLUSIONS = [
  'full_insurance',
  'unlimited_km',
  'additional_driver',
  'child_seat',
  'gps',
  'winter_tyres',
] as const;
export type CarInclusion = (typeof CAR_INCLUSIONS)[number];
export const CAR_INCLUSION_LABELS: Record<CarInclusion, string> = {
  full_insurance: 'Tam sigorta',
  unlimited_km: 'Sınırsız km',
  additional_driver: 'Ek sürücü',
  child_seat: 'Çocuk koltuğu',
  gps: 'Navigasyon',
  winter_tyres: 'Kış lastiği',
};

export const CAR_STATUS_LABELS: Record<ServiceStatus, string> = {
  option: 'Opsiyonda',
  confirmed: 'Onaylandı',
  cancelled: 'İptal',
};

/**
 * Rental days the way rental companies count them: started 24-hour periods from
 * pickup to return, at least one. Without times, whole days between the dates.
 */
export function rentalDays(
  pickupDate: string,
  pickupTime: string | null,
  returnDate: string,
  returnTime: string | null,
): number {
  const from = Date.parse(`${pickupDate}T${pickupTime ?? '00:00'}:00Z`);
  const to = Date.parse(`${returnDate}T${returnTime ?? pickupTime ?? '00:00'}:00Z`);
  return Math.max(1, Math.ceil((to - from) / 86_400_000));
}

const carFields = z.object({
  rentalCompany: optionalText(80),
  carClass: z.enum(CAR_CLASSES).default('compact'),
  model: optionalText(80),
  transmission: z.enum(TRANSMISSIONS).default('manual'),
  inclusions: z
    .array(z.enum(CAR_INCLUSIONS))
    .default([])
    .transform((list) => CAR_INCLUSIONS.filter((i) => list.includes(i))),
  pickupLocation: z.string().trim().min(1, 'Alış yerini yazın').max(160),
  pickupDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Alış tarihini seçin'),
  pickupTime: clock,
  returnLocation: optionalText(160),
  returnDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'İade tarihini seçin'),
  returnTime: clock,
  drivers: z
    .array(z.object({ contactId: id }))
    .min(1, 'Ana sürücüyü seçin')
    .max(4, 'En fazla dört sürücü olabilir'),
  confirmationNumber: z.string().trim().max(40).nullable().optional().transform(blankToNull),
  ...commonFields,
});

function checkCar(c: z.output<typeof carFields>, ctx: z.RefinementCtx) {
  const from = `${c.pickupDate}T${c.pickupTime ?? '00:00'}`;
  const to = `${c.returnDate}T${c.returnTime ?? '23:59'}`;
  if (to <= from) {
    ctx.addIssue({
      code: 'custom',
      path: ['returnDate'],
      message: 'İade alıştan sonra olmalı',
    });
  } else if (
    rentalDays(c.pickupDate, c.pickupTime ?? null, c.returnDate, c.returnTime ?? null) > 365
  ) {
    ctx.addIssue({ code: 'custom', path: ['returnDate'], message: 'Kiralama bir yılı geçemez' });
  }
  const seen = new Set<string>();
  c.drivers.forEach((d, i) => {
    if (seen.has(d.contactId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['drivers', i],
        message: 'Bu sürücü listede zaten var',
      });
    }
    seen.add(d.contactId);
  });
  checkOption(c, ctx);
}

export const carInput = carFields.superRefine(checkCar);
export type CarInput = z.input<typeof carInput>;

// Extra services -----------------------------------------------------------------------

export const EXTRA_CATEGORIES = [
  'transfer',
  'insurance',
  'visa',
  'tour',
  'event',
  'guide',
  'other',
] as const;
export type ExtraCategory = (typeof EXTRA_CATEGORIES)[number];
export const EXTRA_CATEGORY_LABELS: Record<ExtraCategory, string> = {
  transfer: 'Transfer',
  insurance: 'Seyahat sigortası',
  visa: 'Vize işlemi',
  tour: 'Tur / gezi',
  event: 'Etkinlik bileti',
  guide: 'Rehberlik',
  other: 'Diğer',
};
/** Categories that have a route: from somewhere to somewhere. */
export const EXTRA_ROUTED: readonly ExtraCategory[] = ['transfer', 'tour'];

export const EXTRA_STATUS_LABELS: Record<ServiceStatus, string> = {
  option: 'Opsiyonda',
  confirmed: 'Onaylandı',
  cancelled: 'İptal',
};

const extraFields = z.object({
  category: z.enum(EXTRA_CATEGORIES).default('other'),
  title: z.string().trim().min(1, 'Hizmetin adını yazın').max(160),
  description: optionalText(2000),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarihi seçin'),
  startTime: clock,
  endDate: isoDate
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  fromPlace: optionalText(160),
  toPlace: optionalText(160),
  reference: optionalText(60),
  people: z
    .array(z.object({ contactId: id }))
    .max(99)
    .default([]),
  ...commonFields,
});

function checkExtra(e: z.output<typeof extraFields>, ctx: z.RefinementCtx) {
  if (e.endDate && e.endDate < e.startDate) {
    ctx.addIssue({
      code: 'custom',
      path: ['endDate'],
      message: 'Bitiş tarihi başlangıçtan önce olamaz',
    });
  }
  const seen = new Set<string>();
  e.people.forEach((p, i) => {
    if (seen.has(p.contactId)) {
      ctx.addIssue({ code: 'custom', path: ['people', i], message: 'Bu kişi listede zaten var' });
    }
    seen.add(p.contactId);
  });
  checkOption(e, ctx);
}

export const extraInput = extraFields.superRefine(checkExtra);
export type ExtraInput = z.input<typeof extraInput>;

// Any service --------------------------------------------------------------------------

export const createServiceInput = z.discriminatedUnion('kind', [
  flightFields.extend({ kind: z.literal('flight') }).superRefine(checkFlight),
  hotelFields.extend({ kind: z.literal('hotel') }).superRefine(checkHotel),
  carFields.extend({ kind: z.literal('car') }).superRefine(checkCar),
  extraFields.extend({ kind: z.literal('extra') }).superRefine(checkExtra),
]);
export type CreateServiceInput = z.input<typeof createServiceInput>;

/**
 * A proposal's items: the same forms as a sale's services, but the travellers are
 * optional until the proposal becomes a sale (a quote is often for "two adults").
 */
export const proposalItemInput = z.discriminatedUnion('kind', [
  flightFields
    .extend({
      kind: z.literal('flight'),
      passengers: z.array(passengerInput).max(99).default([]),
    })
    .superRefine(checkFlight),
  hotelFields
    .extend({
      kind: z.literal('hotel'),
      rooms: z
        .array(
          z.object({
            ...roomDetails,
            guests: z
              .array(z.object({ contactId: id }))
              .max(8)
              .default([]),
          }),
        )
        .min(1, 'En az bir oda ekleyin')
        .max(30),
    })
    .superRefine(checkHotel),
  carFields
    .extend({
      kind: z.literal('car'),
      drivers: z
        .array(z.object({ contactId: id }))
        .max(4)
        .default([]),
    })
    .superRefine(checkCar),
  extraFields.extend({ kind: z.literal('extra') }).superRefine(checkExtra),
]);
export type ProposalItemInput = z.input<typeof proposalItemInput>;

/** Editing a flight sends the whole flight; legs and passengers are replaced. */
export const updateFlightInput = flightFields.extend({ version }).superRefine(checkFlight);
/** Editing a hotel sends the whole booking; rooms and guests are replaced. */
export const updateHotelInput = hotelFields.extend({ version }).superRefine(checkHotel);
/** Editing a car sends the whole booking; drivers are replaced. */
export const updateCarInput = carFields.extend({ version }).superRefine(checkCar);
/** Editing an extra sends the whole service; its people are replaced. */
export const updateExtraInput = extraFields.extend({ version }).superRefine(checkExtra);

/** Status wording per kind: a flight is "Biletlendi", a hotel "Onaylandı". */
export const STATUS_LABELS = {
  flight: FLIGHT_STATUS_LABELS,
  hotel: HOTEL_STATUS_LABELS,
  car: CAR_STATUS_LABELS,
  extra: EXTRA_STATUS_LABELS,
} as const;

export const hotelSuggestQuery = z.object({ q: z.string().trim().max(100).default('') });

export const serviceStatusInput = z.object({
  status: z.enum(SERVICE_STATUSES),
  version,
  optionDeadline: z.iso.datetime({ offset: true }).nullable().optional(),
});

export const createSaleInput = z
  .object({
    accountId: id.nullable().optional(),
    contactId: id.nullable().optional(),
    departmentId: id.nullable().optional(),
    /** The company it is opened in; asked for when the member works in several (ADR 0017). */
    companyId: id.nullable().optional(),
    notes: optionalText(4000),
    services: z.array(createServiceInput).max(20).default([]),
  })
  .refine((s) => s.accountId || s.contactId, {
    path: ['customer'],
    message: 'Bir firma ya da kişi seçin',
  });
export type CreateSaleInput = z.input<typeof createSaleInput>;

export const updateSaleInput = z
  .object({
    accountId: id.nullable(),
    contactId: id.nullable(),
    departmentId: id.nullable(),
    ownerUserId: id,
    notes: optionalText(4000),
  })
  .partial()
  .extend({ version });

export const saleListQuery = z.object({
  q: z.string().trim().max(100).optional(),
  status: z.enum(['active', 'completed', 'cancelled', 'option', 'all']).default('all'),
  mine: z.enum(['true', 'false']).optional(),
  accountId: id.optional(),
  contactId: id.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const SALE_STATUS_LABELS = {
  active: 'Aktif',
  completed: 'Tamamlandı',
  cancelled: 'İptal',
} as const;

export const calendarQuery = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  mine: z.enum(['true', 'false']).optional(),
});
